import { useModeAwareQuery, useModeAwareMutation } from "@/hooks/useModeAwareQuery";
import { useApi } from "@/hooks/useApi";
import { useQueryClient } from "@tanstack/react-query";
import type {
  TransactionDocument,
  TransactionDocumentsResponse,
  UploadTransactionDocumentPayload,
} from "@/types/transaction-document";
import {
  DOCUMENT_UPLOAD_FOLDER,
  DOCUMENT_UPLOAD_TYPE,
} from "@/types/transaction-document";
import { useApiConfig } from "@/providers/ApiConfigProvider";

// ---------------------------------------------------------------------------
// Mock data — demo mode
// ---------------------------------------------------------------------------

const mockDocuments: TransactionDocument[] = [
  {
    id: "tdoc-1",
    fileName: "CrystalReportViewer1 (2).pdf",
    fileType: "application/pdf",
    fileSize: 245678,
    documentType: "Sales Ledger Transaction",
    uploadedAt: "2026-04-20T10:30:00Z",
    uploadedByEmail: "admin@TMCProductions.com",
    transactionReference: "INV-0042",
  },
  {
    id: "tdoc-2",
    fileName: "Remittance_Advice_Mar26.pdf",
    fileType: "application/pdf",
    fileSize: 98765,
    documentType: "Remittance Advice",
    uploadedAt: "2026-03-15T09:15:00Z",
    uploadedByEmail: "finance@TMCProductions.com",
    transactionReference: "INV-0042",
  },
  {
    id: "tdoc-3",
    fileName: "Invoice_INV-0042_original.pdf",
    fileType: "application/pdf",
    fileSize: 312400,
    documentType: "Invoice",
    uploadedAt: "2026-02-01T14:45:00Z",
    uploadedByEmail: "admin@TMCProductions.com",
    transactionReference: "INV-0042",
  },
];

const mockDocumentsResponse: TransactionDocumentsResponse = {
  documents: mockDocuments,
  totalCount: mockDocuments.length,
};

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * Fetch all documents linked to a transaction by its reference number.
 * Uses demo mock data when the app is in demo mode.
 */
export function useTransactionDocuments(transactionReference: string | undefined) {
  const { apiGet } = useApi();

  return useModeAwareQuery(
    ["transactionDocuments", transactionReference ?? ""],
    async ({ signal }) => {
      return apiGet<TransactionDocumentsResponse>(
        "/api/v1/documents",
        { transactionRef: transactionReference },
        signal
      );
    },
    mockDocumentsResponse,
    {
      enabled: !!transactionReference,
      staleTime: 60 * 1000,
    }
  );
}

/**
 * Upload a new document linked to a transaction reference.
 * In demo mode the mutation resolves instantly with a mock document.
 */
export function useUploadTransactionDocument() {
  const queryClient = useQueryClient();
  const { apiBaseUrl, getHeaders } = useApiConfig();

  return useModeAwareMutation<
    TransactionDocument,
    Error,
    UploadTransactionDocumentPayload
  >(
    async ({ transactionReference, file }) => {
      const headers = await getHeaders();
      // Remove Content-Type so the browser sets multipart boundary automatically
      const { "Content-Type": _ct, ...headersWithoutCt } =
        headers as Record<string, string>;

      const formData = new FormData();
      formData.append("file", file);
      formData.append("transactionRef", transactionReference);
      // Passed silently — predetermined for Credit Control, not user-selectable
      formData.append("documentType", DOCUMENT_UPLOAD_TYPE);
      formData.append("folder", DOCUMENT_UPLOAD_FOLDER);

      const response = await fetch(`${apiBaseUrl}/api/v1/documents`, {
        method: "POST",
        headers: headersWithoutCt,
        body: formData,
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error(`Upload failed (HTTP ${response.status})`);
      }

      return response.json() as Promise<TransactionDocument>;
    },
    // Demo mode — return a mock document immediately
    ({ file, transactionReference }) => ({
      id: `tdoc-demo-${Date.now()}`,
      fileName: file.name,
      fileType: file.type,
      fileSize: file.size,
      documentType: DOCUMENT_UPLOAD_TYPE,
      uploadedAt: new Date().toISOString(),
      uploadedByEmail: "demo@example.com",
      transactionReference,
    }),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries({
          queryKey: ["transactionDocuments", variables.transactionReference],
        });
      },
    }
  );
}

/**
 * Delete a document by ID.
 * In demo mode the mutation resolves immediately (no API call).
 */
export function useDeleteTransactionDocument() {
  const queryClient = useQueryClient();
  const { apiBaseUrl, getHeaders } = useApiConfig();

  return useModeAwareMutation<
    void,
    Error,
    { documentId: string; transactionReference: string }
  >(
    async ({ documentId }) => {
      const headers = await getHeaders();
      const response = await fetch(`${apiBaseUrl}/api/v1/documents/${documentId}`, {
        method: "DELETE",
        headers: headers as HeadersInit,
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error(`Delete failed (HTTP ${response.status})`);
      }
    },
    // Demo mode — no-op
    () => undefined,
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries({
          queryKey: ["transactionDocuments", variables.transactionReference],
        });
      },
    }
  );
}
